import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isMainDomain, getTenantSlug } from 'src/utils/domainContext';
import { validateTenantStatus } from 'src/redux/slices/tenantValidationSlice';
import { SplashScreen } from 'src/components/loading-screen';
import TenantNotFoundPage from 'src/pages/tenant/TenantNotFoundPage';
import TenantSuspendedPage from 'src/pages/tenant/TenantSuspendedPage';

// ------------------------------------------------
// TENANT-DOMAIN-ONLY GUARD
// Wraps routes that should only be accessible on a
// valid, active tenant subdomain (e.g. acme.merudesk.com).
//
// Behaviour:
//  • Main domain → hard redirect to landing page
//  • Tenant subdomain → validate via API, then:
//       active    → render children
//       suspended → show suspended page
//       not-found → show not-found page
//       loading   → splash screen
// ------------------------------------------------
export default function TenantDomainOnly({ children }) {
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.tenantValidation);
  const onMain = isMainDomain();

  // Trigger validation once when mounted on a tenant subdomain
  useEffect(() => {
    if (!onMain && status === 'idle') {
      dispatch(validateTenantStatus());
    }
  }, [onMain, status, dispatch]);

  // ---- Main domain visitor: redirect to landing page ----
  if (onMain) {
    // Use Navigate for SPA redirect within the same origin
    // eslint-disable-next-line global-require
    const { Navigate } = require('react-router-dom');
    return <Navigate to="/" replace />;
  }

  // ---- Tenant subdomain: status-driven rendering ----
  if (status === 'idle' || status === 'loading') {
    return <SplashScreen />;
  }

  if (status === 'not-found') {
    return <TenantNotFoundPage />;
  }

  if (status === 'suspended') {
    return <TenantSuspendedPage />;
  }

  // status === 'active' (or 'error' — let the app attempt to function)
  return <>{children}</>;
}

TenantDomainOnly.propTypes = {
  children: PropTypes.node,
};
